import React, {useState} from 'react';
import { Box, Grid2 as Grid, TableContainer, Table, TableHead, TableRow, TableCell, useMediaQuery, useTheme, Paper, Typography, TableSortLabel, TableBody, styled, Stack} from '@mui/material';
import { CreditCardChargeItem } from '../../../types/reports/creditCardChargeItem';
import { visuallyHidden } from '@mui/utils';

interface Props {
   tableRows?:CreditCardChargeItem[];
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));


const CreditCardChargesScheduleTable = (props: Props): JSX.Element => {

    const theme = useTheme();
    const [expenditureRows, setExpenditureRows] = useState<CreditCardChargeItem[]>(props.tableRows ?? []);
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState<'desc' | 'asc'>('asc');
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));


    const sortByAmount = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return b.amountValue - a.amountValue;
            } else {
                return a.amountValue - b.amountValue;
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByDate = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            } else {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByName = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return b.fullNameReverse.localeCompare(a.fullNameReverse);
            } else {
                return a.fullNameReverse.localeCompare(b.fullNameReverse);
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const updateSort = (column: string) => {
        setOrderBy(column);
    }

    return (
        <Grid container spacing={1} marginTop={1} marginBottom={1}>
         <TableContainer component={Paper}>
             <Table aria-label="independent expenditures table" size="small">
                 <TableHead>
                     <TableRow>
                         <TableCell onClick={sortByDate} onMouseEnter={()=>updateSort('date')}>
                            <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                Date
                            </Typography>
                         </TableCell>
                         <TableCell onClick={sortByName} onMouseEnter={()=>updateSort('name')}>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={sortByName}
                            >
                                <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable'>
                                    Name/Address
                                </Typography>
                                {orderBy === 'name' ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                         </TableCell>
                         {
                            isSM &&
                            <TableCell align='right' onClick={sortByAmount}>
                                <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                    Amount
                                </Typography>
                            </TableCell>
                         }
                         <TableCell>
                             <Typography variant={'subtitle2'} fontWeight={600}>
                                Purpose
                             </Typography>
                         </TableCell>
                         <TableCell>
                             <Typography variant={'subtitle2'} fontWeight={600}>
                                Authorized By
                             </Typography>
                         </TableCell>
                         {
                            !isSM &&
                            <TableCell onClick={sortByAmount} align='right'>
                                <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                     Amount
                                </Typography>
                            </TableCell>
                         }
                     </TableRow>
                 </TableHead>
                 <TableBody>
                     {expenditureRows?.map((expenditure) => (
                         <StyledTableRow key={expenditure.id}>
                             <TableCell>
                                 <Typography variant={'body2'}>
                                     {expenditure.date}
                                 </Typography>
                             </TableCell>
                             <TableCell>
                                <Stack>
                                    <Typography variant="body2">
                                        {expenditure.fullNameReverse} {expenditure.vendor}
                                    </Typography>
                                    <Typography component="i"  variant="body2">{expenditure.fullAddress}</Typography>
                                </Stack>       
                            </TableCell>
                             {
                                isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {expenditure.amount}
                                    </Typography>
                                </TableCell>
                             }
                             <TableCell>
                                <Typography variant="body2">{expenditure.description} {expenditure.purpose}</Typography>
                             </TableCell>
                             <TableCell>
                                <Typography variant="body2">{expenditure.authorizedBy}</Typography>
                             </TableCell>
                             {
                                !isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {expenditure.amount}
                                    </Typography>
                                </TableCell>
                             }   
                         </StyledTableRow>
                    ))}
                 </TableBody>
             </Table>
         </TableContainer>
        </Grid>
    );

    /**
     * 
     * return (
        <Box 
            sx={{ 
            width: '100%',
            '.MuiDataGrid-columnHeaderTitle': { 
            fontWeight: 'bold !important',
            },
        }}>
        <DataGrid 
            rows={props.tableRows} 
            columns={tableColumns}
            columnVisibilityModel={{
                amount: isSM? true: false,
                amountDisplay: isSM? false: true,
            }}
            getRowHeight={() => 'auto'}
            getRowId={(row) => row.guid}
            disableColumnMenu
            slots={{ toolbar: QuickSearchToolbar }}
            hideFooterPagination={totalCount < 100? true: false}
            sx={{ 
                '.MuiDataGrid-cell': {
                  alignItems: 'center',
                  whiteSpace: 'normal', 
                  wordBreak: 'break-word',
                  paddingTop: '3px!important',
                  paddingBottom: '3px!important',
                },
              }}
        />
    </Box>
    );
     * 
     */

}

export default CreditCardChargesScheduleTable;